import { WorkOutlineOutlined } from '@mui/icons-material'
import { useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useInView } from 'react-intersection-observer'
import { SmallTextBold } from '@front/components/data-display/typography/Typography'
import { useDebounce } from '@front/components/hooks/useDebounce'
import { JobCardSkeleton } from '../../shared/job-card/JobCardSkeleton'
import { useGetJobAds } from '../../hooks/useGetJobAds'
import type { JobAdsFormType } from '../../../types/career.types'
import { useGetPublicCareerSiteQuery } from '@front/api/service-career-neo/useGetPublicCareerSiteQuery'
import { Stack, useTheme } from '@mui/material'
import { Spacer } from '../../shared/spacer/Spacer.style'
import type { ContractType } from '@front/api/service-career/job-listing.types'
import {
  useJobAdsFiltersStorage,
  defaultValues,
} from '../../hooks/useJobAdsFiltersStorage'
import { getRuntimeConfig } from '@front/env/config/career'
import { EmptyState } from '../../shared/empty-state/EmptyState'
import SpontaneousPromptCard from '../../shared/spontaneous-prompt-card/SpontaneousPromptCard'
import JobCard from '../../shared/job-card/JobCard'

type Props = { slug: string }

export const ResultsContainer = ({ slug }: Props) => {
  const {
    public: { monorepoBaseUrl },
  } = getRuntimeConfig()
  const theme = useTheme()
  const { t } = useTranslation([
    'main.common',
    'main.worker',
    'main.talent',
    'network.career_website',
  ])

  const { data: institutionData } = useGetPublicCareerSiteQuery(slug)

  const { ref, inView } = useInView() // used to trigger fetchNextPage when inView
  const { setValue, watch } = useFormContext<JobAdsFormType>()
  const { resetStorageData } = useJobAdsFiltersStorage()

  const handleSpontaneousApply = () => {
    if (!institutionData?.institutionId) {
      return
    }

    window.open(
      `${monorepoBaseUrl}/front/worker/profile/institutions/${institutionData?.institutionId}/presentation?spontaneousApply=true&fromSearch=true&referer=career_site`,
      '_blank',
      'noopener,noreferrer',
    )
  }

  const debouncedQueryInput = useDebounce(watch('searchQuery'), 500)
  const departmentFilter = watch('departmentCodeQuery')
  const sortByFilter = watch('sortBy')
  const sortOrderFilter = watch('sortOrder')
  const contractTypeFilter = watch('contractType')
  const contractDurationFilter = watch('contractDuration')
  const yearsOfExperienceFilter = watch('yearsOfExperience')
  const institutionTypeFilter = watch('institutionType')
  const contextFilter = watch('context')

  const newData: JobAdsFormType = {
    departmentCodeQuery: departmentFilter || undefined,
    searchQuery: debouncedQueryInput || undefined,
    sortBy: sortByFilter,
    sortOrder: sortOrderFilter,
    contractType: contractTypeFilter || undefined,
    contractDuration: contractDurationFilter || undefined,
    yearsOfExperience: yearsOfExperienceFilter || undefined,
    institutionType: institutionTypeFilter || undefined,
    context: contextFilter,
  }
  const hasFilters = Object.keys(newData)
    .filter((key) => key !== 'sortBy' && key !== 'sortOrder')
    .some((key) => Boolean(newData[key as keyof JobAdsFormType]))

  const {
    fetchNextPage,
    hasNextPage,
    isError,
    isFetchingNextPage,
    isLoading,
    jobOffersList,
    refetch,
    total,
  } = useGetJobAds(institutionData?.institutionUuid as string, newData)

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleCleanAllFilters = () => {
    Object.keys(newData).forEach((key) => {
      const k = key as keyof JobAdsFormType
      setValue(k, defaultValues[k])
    })
    resetStorageData()
  }

  if (isLoading) {
    return (
      <>
        <SmallTextBold
          sx={{ color: theme.palette.text.helper, py: 2, mb: 1 }}
          data-testid="JobAds__total"
        >
          {t('main.common:loading')}
        </SmallTextBold>
        <JobCardSkeleton />
      </>
    )
  }

  if (isError) {
    return (
      <EmptyState
        button={{
          variant: 'primary',
          onClick: () => void refetch(),
          children: 'Réessayer' /**@TranslationNeeded */,
        }}
        data-testid="JobOffers__JobOffersContainer__EmptyStateError"
        icon={<WorkOutlineOutlined fontSize="large" />}
        message={t('main.common:errors.data_fetching_error')}
        title={t('main.common:500_error.title')}
      />
    )
  }

  if (jobOffersList?.length) {
    return (
      <>
        <SmallTextBold
          sx={{ color: theme.palette.text.helper, py: 2, mb: 1 }}
          data-testid="JobAds__total"
        >
          {t(`main.worker:offers.subtitle${total === 1 ? '_1' : ''}`, {
            offer_number: total,
          })}
        </SmallTextBold>
        <Stack
          sx={{
            gap: 2,
          }}
        >
          {jobOffersList?.map((job) => (
            <JobCard
              uuid={job.uuid}
              slug={slug}
              contractTypes={job.contractTypes as ContractType[]}
              context={job.context}
              jobs={job.jobs}
              title={job.title}
              publishedAt={job.publishedAt}
            />
          ))}
        </Stack>
        {isFetchingNextPage && <JobCardSkeleton />}
        {/* this div triggers fetchNextPage when inView */}
        {!isFetchingNextPage && hasNextPage && (
          <div ref={ref} className="mt-4">
            <JobCardSkeleton />
          </div>
        )}
        {institutionData && (
          <Stack sx={{ marginBottom: { xs: 5, md: 0 } }}>
            <Spacer spacing={2} />
            <SpontaneousPromptCard
              apply={handleSpontaneousApply}
              name={institutionData?.name ?? 'Institution'}
            />
          </Stack>
        )}
      </>
    )
  }

  // no results with filters
  if (hasFilters) {
    return (
      <EmptyState
        button={{
          variant: 'primary',
          onClick: handleCleanAllFilters,
          children: t('main.worker:offers.empty_state.filter_no_result.button'),
        }}
        data-testid="JobOffers__JobOffersContainer__EmptyStateNoResultsWithFilters"
        icon={<WorkOutlineOutlined fontSize="large" />}
        message={t('main.worker:offers.empty_state.filter_no_result.text')}
        title={t('main.worker:offers.empty_state.filter_no_result.title')}
      />
    )
  }

  // no results even without filters
  return (
    <EmptyState
      data-testid="JobOffers__JobOffersContainer__EmptyStateNoResultsNoFilters"
      icon={<WorkOutlineOutlined fontSize="large" />}
      message={t('main.talent:job_offer.list.empty_title')}
      title={t('main.worker:offers.empty_state.filter_no_result.title')}
    />
  )
}
